<template>
  <transition name="debtia-overlay">
    <div
      v-if="show"
      class="debtia-loading-overlay"
    >
      <div class="debtia-loading-backdrop" />
      <div class="debtia-loading-content">
        <div class="debtia-loading-spinner">
          <div class="debtia-spinner-ring" />
          <div class="debtia-spinner-ring debtia-spinner-ring-2" />
          <div class="debtia-spinner-dot" />
        </div>
        <p
          v-if="text"
          class="debtia-loading-text"
        >
          {{ text }}
        </p>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'DebtiaLoading',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    text: {
      type: String,
      default: 'Indlæser...',
    },
  },
}
</script>

<style lang="scss">
// ==============================
// DEBTIA LOADING OVERLAY - Global glassmorphism loader
// ==============================
$debtia-green: #088c76;

.debtia-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

// Backdrop: blur is INSTANT, only background-color fades in
.debtia-loading-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(16px) saturate(1.4);
  -webkit-backdrop-filter: blur(16px) saturate(1.4);
  background: rgba(7, 21, 33, 0.45);
  transition: background 0.4s ease;
}

// Content card fades + slides up
.debtia-loading-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  padding: 2.5rem 3rem;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.15);
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.debtia-loading-spinner {
  position: relative;
  width: 48px;
  height: 48px;
}

.debtia-spinner-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: $debtia-green;
  animation: debtiaSpinnerRotate 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.debtia-spinner-ring-2 {
  inset: 6px;
  border-top-color: rgba(8, 140, 118, 0.4);
  animation-delay: -0.4s;
  animation-direction: reverse;
}

.debtia-spinner-dot {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: $debtia-green;
  animation: debtiaSpinnerPulse 1.2s ease-in-out infinite;
}

@keyframes debtiaSpinnerRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes debtiaSpinnerPulse {
  0%, 100% { opacity: 0.4; transform: translate(-50%, -50%) scale(0.8); }
  50% { opacity: 1; transform: translate(-50%, -50%) scale(1.2); }
}

.debtia-loading-text {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9rem;
  font-weight: 400;
  letter-spacing: 0.02em;
  margin: 0;
}

// ==============================
// ENTER: blur instant, bg + content fade in
// ==============================
.debtia-overlay-enter-active {
  // The overlay container itself appears instantly (no opacity fade)
  transition: none;

  // Backdrop bg fades in (blur is already at full)
  .debtia-loading-backdrop {
    transition: background 0.4s ease;
  }

  // Content card fades + slides up
  .debtia-loading-content {
    transition: opacity 0.35s ease 0.05s, transform 0.35s ease 0.05s;
  }
}

.debtia-overlay-enter {
  // Overlay container: fully visible immediately (blur works from frame 1)
  opacity: 1;

  .debtia-loading-backdrop {
    background: rgba(7, 21, 33, 0);
  }

  .debtia-loading-content {
    opacity: 0;
    transform: translateY(12px);
  }
}

// ==============================
// LEAVE: everything fades out together
// ==============================
.debtia-overlay-leave-active {
  transition: opacity 0.4s ease;
}

.debtia-overlay-leave-to {
  opacity: 0;
}

// ==============================
// Dark mode
// ==============================
.dark-layout .debtia-loading-backdrop {
  background: rgba(4, 10, 18, 0.55);
}

.dark-layout .debtia-overlay-enter .debtia-loading-backdrop {
  background: rgba(4, 10, 18, 0);
}

.dark-layout .debtia-loading-content {
  background: rgba(10, 30, 50, 0.35);
  border-color: rgba(8, 140, 118, 0.15);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05);
}
</style>
